
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Award, Edit, ArrowLeft, Loader2, Calendar, Clock, MapPin, CheckCircle, User, DollarSign, Globe, Tag, File } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { LazyImage } from '@/components/ui/lazy-image';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface Tag {
  id: string;
  name: string;
}

interface ItineraryDay {
  id: string;
  day_number: number;
  title: string;
  description: string | null;
  location: string | null;
}

export default function PremiumItineraryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState("overview");

  // Fetch premium itinerary data
  const { data: itinerary, isLoading, error } = useQuery({
    queryKey: ['premium-itinerary', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('premium_itineraries')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch itinerary days
  const { data: days, isLoading: isDaysLoading } = useQuery({
    queryKey: ['premium-itinerary-days', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('premium_itinerary_days')
        .select('*')
        .eq('premium_itinerary_id', id)
        .order('day_number', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!id
  });

  // Fetch tags for the itinerary
  const { data: tagData, isLoading: isTagsLoading } = useQuery({
    queryKey: ['premium-itinerary-tags', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('premium_itinerary_tags')
        .select('tags:tag_id(id, name)')
        .eq('premium_itinerary_id', id);
      
      if (error) throw error;
      return data?.map(item => (item.tags as unknown as Tag)) || [];
    },
    enabled: !!id
  });

  // Format currency for display
  const formatCurrency = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Handle publishing itinerary
  const handlePublish = async () => {
    try {
      const { error } = await supabase
        .from('premium_itineraries')
        .update({ status: 'published' })
        .eq('id', id);
      
      if (error) throw error;
      
      toast.success("Itinerary published successfully");
      window.location.reload();
    } catch (error: any) {
      console.error("Error publishing itinerary:", error);
      toast.error(error.message || "Failed to publish itinerary");
    }
  };

  // Handle unpublishing itinerary
  const handleUnpublish = async () => {
    try {
      const { error } = await supabase
        .from('premium_itineraries')
        .update({ status: 'draft' })
        .eq('id', id);
      
      if (error) throw error;
      
      toast.success("Itinerary unpublished");
      window.location.reload();
    } catch (error: any) {
      console.error("Error unpublishing itinerary:", error);
      toast.error(error.message || "Failed to unpublish itinerary");
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard/premium-itineraries')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Premium Itineraries
          </Button>
        </div>

        <div className="flex justify-center py-12">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Error state
  if (error || !itinerary) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard/premium-itineraries')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Premium Itineraries
          </Button>
        </div>

        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-destructive">Error loading itinerary details</p>
            <Button 
              variant="outline" 
              className="mt-4" 
              onClick={() => navigate('/dashboard/premium-itineraries')}
            >
              Return to Premium Itineraries
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/dashboard/premium-itineraries')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Premium Itineraries
        </Button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center gap-2">
          <Award className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">{itinerary.title}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge variant={itinerary.status === 'published' ? 'default' : 'secondary'}>
            {itinerary.status === 'published' ? 'Published' : 'Draft'}
          </Badge>
          {itinerary.is_featured && (
            <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
              Featured
            </Badge>
          )}
          <Button 
            onClick={() => navigate(`/dashboard/premium-itineraries/${id}/edit`)}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit Itinerary
          </Button>
        </div>
      </div>

      <Tabs defaultValue={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid grid-cols-3 w-full md:w-auto">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="days">Days ({days?.length || 0})</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="mt-4 space-y-6">
          <Card>
            <CardContent className="p-0">
              {itinerary.featured_image && (
                <div className="w-full h-64 md:h-96 overflow-hidden">
                  <LazyImage 
                    src={itinerary.featured_image} 
                    alt={itinerary.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              
              <div className="p-6">
                <div className="flex flex-wrap gap-4 mb-4 text-muted-foreground">
                  {itinerary.destination && (
                    <div className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      <span>{itinerary.destination}</span>
                    </div>
                  )}
                  {itinerary.duration && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{itinerary.duration} days</span>
                    </div>
                  )}
                  {itinerary.best_season && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      <span>Best: {itinerary.best_season}</span>
                    </div>
                  )}
                </div>
                
                <div className="mb-6">
                  <Badge className="text-lg px-3 py-1.5 mb-4 bg-primary">
                    {formatCurrency(itinerary.price, itinerary.currency)}
                  </Badge>
                  
                  {itinerary.description ? (
                    <div className="prose prose-sm max-w-none dark:prose-invert">
                      <RichTextViewer content={itinerary.description} />
                    </div>
                  ) : (
                    <p className="text-muted-foreground italic">No description provided</p>
                  )}
                </div>
                
                {!isTagsLoading && tagData && tagData.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm font-medium mb-2">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {tagData.map(tag => (
                        <Badge variant="secondary" key={tag.id}>{tag.name}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
                <Card>
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <Clock className="h-8 w-8 text-primary mb-2" />
                    <p className="text-xl font-bold">{itinerary.duration || 0}</p>
                    <p className="text-sm text-muted-foreground">Days</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <MapPin className="h-8 w-8 text-primary mb-2" />
                    <p className="text-xl font-bold whitespace-nowrap overflow-hidden text-ellipsis max-w-36">
                      {itinerary.destination || "Not specified"}
                    </p>
                    <p className="text-sm text-muted-foreground">Destination</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <DollarSign className="h-8 w-8 text-primary mb-2" />
                    <p className="text-xl font-bold">{formatCurrency(itinerary.price, itinerary.currency)}</p>
                    <p className="text-sm text-muted-foreground">Price</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4 flex flex-col items-center justify-center text-center">
                    <Tag className="h-8 w-8 text-primary mb-2" />
                    <p className="text-xl font-bold">{tagData?.length || 0}</p>
                    <p className="text-sm text-muted-foreground">Tags</p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="days" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Itinerary Days</CardTitle>
                <CardDescription>Day-by-day breakdown of this premium itinerary</CardDescription>
              </div>
              <Button onClick={() => navigate(`/dashboard/premium-itineraries/${id}/edit`)} variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Edit Days
              </Button>
            </CardHeader>
            <CardContent>
              {isDaysLoading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : days && days.length > 0 ? (
                <div className="space-y-8">
                  {days.map((day: ItineraryDay, index: number) => (
                    <div key={day.id} className="space-y-2">
                      {index > 0 && <Separator className="my-6" />}
                      <div className="flex items-start gap-4">
                        <Badge className="px-2 py-1 min-w-10 text-center">Day {day.day_number}</Badge>
                        <div className="space-y-2 flex-1">
                          <h3 className="text-xl font-semibold">{day.title}</h3>
                          {day.location && (
                            <div className="flex items-center text-muted-foreground text-sm">
                              <MapPin className="h-4 w-4 mr-1" /> {day.location}
                            </div>
                          )}
                          {day.description ? (
                            <div className="prose prose-sm max-w-none dark:prose-invert mt-2">
                              <RichTextViewer content={day.description} />
                            </div>
                          ) : (
                            <p className="text-muted-foreground italic">No description for this day</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border rounded-md bg-muted/20">
                  <p className="text-muted-foreground mb-4">No days have been added to this itinerary yet.</p>
                  <Button onClick={() => navigate(`/dashboard/premium-itineraries/${id}/edit`)} variant="outline">
                    <Edit className="h-4 w-4 mr-2" />
                    Add Days
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings" className="mt-4 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
              <CardDescription>
                Control the visibility of this premium itinerary
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-medium">Current status: {' '}
                    <Badge variant={itinerary.status === 'published' ? 'default' : 'secondary'}>
                      {itinerary.status === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {itinerary.status === 'published' 
                      ? 'This itinerary is visible to the public' 
                      : 'This itinerary is not visible to the public'}
                  </p>
                </div>
                
                {itinerary.status === 'published' ? (
                  <Button variant="outline" onClick={handleUnpublish}>
                    Unpublish
                  </Button>
                ) : (
                  <Button onClick={handlePublish}>
                    Publish
                  </Button>
                )}
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-medium">Featured Status</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {itinerary.is_featured 
                      ? 'This itinerary is featured on the homepage' 
                      : 'This itinerary is not featured'}
                  </p>
                </div>
                
                <Button 
                  variant={itinerary.is_featured ? "outline" : "default"}
                  onClick={async () => {
                    try {
                      const { error } = await supabase
                        .from('premium_itineraries')
                        .update({ is_featured: !itinerary.is_featured })
                        .eq('id', id);
                      
                      if (error) throw error;
                      
                      toast.success(
                        itinerary.is_featured 
                          ? "Itinerary removed from featured" 
                          : "Itinerary added to featured"
                      );
                      window.location.reload();
                    } catch (error: any) {
                      console.error("Error updating featured status:", error);
                      toast.error(error.message || "Failed to update featured status");
                    }
                  }}
                >
                  {itinerary.is_featured ? 'Remove from Featured' : 'Set as Featured'}
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Danger Zone</CardTitle>
            </CardHeader>
            <CardContent>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">Delete This Itinerary</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete the premium itinerary
                      and all associated days.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      onClick={async () => {
                        try {
                          // Delete all days first
                          await supabase
                            .from('premium_itinerary_days')
                            .delete()
                            .eq('premium_itinerary_id', id);
                          
                          // Then delete the itinerary
                          await supabase
                            .from('premium_itineraries')
                            .delete()
                            .eq('id', id);
                          
                          toast.success("Itinerary deleted successfully");
                          navigate('/dashboard/premium-itineraries');
                        } catch (error) {
                          console.error("Error deleting itinerary:", error);
                          toast.error("Failed to delete itinerary");
                        }
                      }}
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
